import { useRef } from 'react'
import { AxiosError } from 'axios'
import {
  getComplianceImageUploadUrl,
  getComplianceImageProxyUrl,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { Editor } from '@tiptap/core'
import { useEditorState } from '@tiptap/react'
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Minus,
  Undo2,
  Redo2,
  RemoveFormatting,
  CodeSquare,
  CornerDownLeft,
  ImagePlus,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import { uploadFileToSignedOssUrl } from '@/shared/lib/uploadFileToSignedOssUrl'

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp']
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024 // 5MB

function getImageKey(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png'
  const safeExt = IMAGE_EXTENSIONS.includes(ext) ? ext : 'png'
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}.${safeExt}`
}

export function DocumentBar({ children }: { children: React.ReactNode }) {
  return (
    <div className='flex flex-wrap items-center gap-1 border-b bg-muted/30 px-2 py-1.5'>
      {children}
    </div>
  )
}

const defaultEditorToolbarState = {
  isBold: false,
  canBold: false,
  isItalic: false,
  canItalic: false,
  isStrike: false,
  canStrike: false,
  isCode: false,
  canCode: false,
  canClearMarks: false,
  isHeading1: false,
  isHeading2: false,
  isHeading3: false,
  isHeading4: false,
  isHeading5: false,
  isHeading6: false,
  isBulletList: false,
  isOrderedList: false,
  isCodeBlock: false,
  canUndo: false,
  canRedo: false,
}

type DocumentMenuBarProps = {
  editor: Editor
  imageUpload?: { complianceId: string }
  variant?: 'full' | 'minimal'
}

function isEditorToolbarReady(editor: Editor | null | undefined): editor is Editor {
  return (
    editor != null &&
    !editor.isDestroyed &&
    typeof editor.can === 'function'
  )
}

function MenuButton({
  onClick,
  disabled,
  active,
  title,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  active?: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'flex size-8 items-center justify-center rounded border border-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50',
        active && 'bg-muted text-foreground'
      )}
    >
      {children}
    </button>
  )
}

function EditorImageUploadButton({
  editor,
  imageUpload,
}: {
  editor: Editor
  imageUpload: { complianceId: string }
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <>
      <input
        ref={fileInputRef}
        type='file'
        accept={IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
        className='hidden'
        onChange={async (event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file == null) {
            return
          }
          if (file.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error('Image must be 5MB or smaller')
            return
          }
          const key = getImageKey(file)
          try {
            const { url } = await getComplianceImageUploadUrl(
              imageUpload.complianceId,
              key
            )
            const contentType = file.type || 'image/png'
            await uploadFileToSignedOssUrl(url, file, {
              contentType,
              context: {
                feature: 'compliance-image',
                fileName: file.name,
                fileSizeBytes: file.size,
                contentType,
                complianceId: imageUpload.complianceId,
              },
            })
            const pathInMarkdown = `compliance-docs/${imageUpload.complianceId}/${key}`
            const proxyUrl = getComplianceImageProxyUrl(
              imageUpload.complianceId,
              pathInMarkdown
            )
            editor.chain().focus().setImage({ src: proxyUrl }).run()
          } catch (err) {
            let message = 'Failed to upload image'
            if (err instanceof AxiosError) {
              const data = err.response?.data as
                | { message?: string; error?: string }
                | undefined
              message =
                data?.message ??
                data?.error ??
                (err.code === 'ERR_NETWORK'
                  ? 'Network error. Check your connection.'
                  : err.message)
            } else if (err instanceof Error) {
              message = err.message
            }
            toast.error(message)
            console.error('[DocumentMenuBar] Image upload error:', err)
          }
        }}
      />
      <MenuButton
        onClick={() => fileInputRef.current?.click()}
        title='Insert image'
      >
        <ImagePlus className='size-4' />
      </MenuButton>
    </>
  )
}

export function DocumentMenuBar({
  editor,
  imageUpload,
  variant = 'full',
}: DocumentMenuBarProps) {
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const ed = ctx.editor
      if (!isEditorToolbarReady(ed)) {
        return defaultEditorToolbarState
      }
      return {
        isBold: ed.isActive('bold') ?? false,
        canBold: ed.can().chain().toggleBold().run() ?? false,
        isItalic: ed.isActive('italic') ?? false,
        canItalic: ed.can().chain().toggleItalic().run() ?? false,
        isStrike: ed.isActive('strike') ?? false,
        canStrike: ed.can().chain().toggleStrike().run() ?? false,
        isCode: ed.isActive('code') ?? false,
        canCode: ed.can().chain().toggleCode().run() ?? false,
        canClearMarks: ed.can().chain().unsetAllMarks().run() ?? false,
        isHeading1: ed.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ed.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ed.isActive('heading', { level: 3 }) ?? false,
        isHeading4: ed.isActive('heading', { level: 4 }) ?? false,
        isHeading5: ed.isActive('heading', { level: 5 }) ?? false,
        isHeading6: ed.isActive('heading', { level: 6 }) ?? false,
        isBulletList: ed.isActive('bulletList') ?? false,
        isOrderedList: ed.isActive('orderedList') ?? false,
        isCodeBlock: ed.isActive('codeBlock') ?? false,
        canUndo: ed.can().chain().undo().run() ?? false,
        canRedo: ed.can().chain().redo().run() ?? false,
      }
    },
  })

  if (!isEditorToolbarReady(editor)) {
    return null
  }

  if (variant === 'minimal') {
    return (
      <div className='flex items-center gap-0.5 rounded-md border bg-background p-0.5 shadow-sm'>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editorState.canBold}
          active={editorState.isBold}
          title='Bold'
        >
          <Bold className='size-4' />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editorState.canItalic}
          active={editorState.isItalic}
          title='Italic'
        >
          <Italic className='size-4' />
        </MenuButton>
        {imageUpload != null ? (
          <EditorImageUploadButton editor={editor} imageUpload={imageUpload} />
        ) : null}
      </div>
    )
  }

  return (
    <DocumentBar>
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editorState.canBold}
        active={editorState.isBold}
        title='Bold'
      >
        <Bold className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editorState.canItalic}
        active={editorState.isItalic}
        title='Italic'
      >
        <Italic className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editorState.canStrike}
        active={editorState.isStrike}
        title='Strikethrough'
      >
        <Strikethrough className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={!editorState.canCode}
        active={editorState.isCode}
        title='Inline code'
      >
        <Code className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().unsetAllMarks().run()}
        disabled={!editorState.canClearMarks}
        title='Clear formatting'
      >
        <RemoveFormatting className='size-4' />
      </MenuButton>

      <span className='mx-1 h-4 w-px bg-border' aria-hidden />

      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editorState.isHeading1}
        title='Heading 1'
      >
        <span className='flex items-center gap-0.5'>
          <Heading1 className='size-4' />
          <span className='text-[10px] font-medium'>1</span>
        </span>
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editorState.isHeading2}
        title='Heading 2'
      >
        <span className='flex items-center gap-0.5'>
          <Heading2 className='size-4' />
          <span className='text-[10px] font-medium'>2</span>
        </span>
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editorState.isHeading3}
        title='Heading 3'
      >
        <span className='flex items-center gap-0.5'>
          <Heading3 className='size-4' />
          <span className='text-[10px] font-medium'>3</span>
        </span>
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
        active={editorState.isHeading4}
        title='Heading 4'
      >
        <span className='flex items-center gap-0.5'>
          <Heading3 className='size-4' />
          <span className='text-[10px] font-medium'>4</span>
        </span>
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
        active={editorState.isHeading5}
        title='Heading 5'
      >
        <span className='text-xs font-medium'>H5</span>
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
        active={editorState.isHeading6}
        title='Heading 6'
      >
        <span className='text-xs font-medium'>H6</span>
      </MenuButton>

      <span className='mx-1 h-4 w-px bg-border' aria-hidden />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editorState.isBulletList}
        title='Bullet list'
      >
        <List className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editorState.isOrderedList}
        title='Ordered list'
      >
        <ListOrdered className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        active={editorState.isCodeBlock}
        title='Code block'
      >
        <CodeSquare className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title='Horizontal rule'
      >
        <Minus className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setHardBreak().run()}
        title='Hard break'
      >
        <CornerDownLeft className='size-4' />
      </MenuButton>
      {imageUpload != null ? (
        <EditorImageUploadButton editor={editor} imageUpload={imageUpload} />
      ) : null}

      <span className='mx-1 h-4 w-px bg-border' aria-hidden />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editorState.canUndo}
        title='Undo'
      >
        <Undo2 className='size-4' />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editorState.canRedo}
        title='Redo'
      >
        <Redo2 className='size-4' />
      </MenuButton>
    </DocumentBar>
  )
}
